import React, { useState, useEffect } from 'react';
import authService from '../../services/authService';
import './Admin.css';

const CreditHistory = ({ organizationId, teamId, accountId }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Filters
  const [transactionType, setTransactionType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [search, setSearch] = useState('');
  
  // Detail modal
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    loadTransactions();
  }, [page, pageSize, sortBy, sortOrder, transactionType, startDate, endDate, search, organizationId, teamId, accountId]);

  const loadTransactions = async () => {
    setLoading(true);
    setError('');
    try {
      const t = authService.getToken();
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        sortBy,
        sortOrder
      });
      
      if (organizationId) params.append('organizationId', organizationId);
      if (teamId) params.append('teamId', teamId);
      if (accountId) params.append('accountId', accountId);
      if (transactionType) params.append('transactionType', transactionType);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (search) params.append('search', search);

      const res = await fetch(`/api/credits/history?${params}`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to load credit history');
      }

      const data = await res.json();
      setTransactions(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message || 'Failed to load credit history');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handleRowClick = async (transactionId) => {
    try {
      const t = authService.getToken();
      const res = await fetch(`/api/credits/history/${transactionId}`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });

      if (res.ok) {
        const data = await res.json();
        setSelectedTransaction(data);
        setShowDetailModal(true);
      }
    } catch (e) {
      setError(e.message || 'Failed to load transaction details');
    }
  };

  const handleExport = () => {
    try {
      const t = authService.getToken();
      const params = new URLSearchParams({
        page: '1',
        pageSize: '10000', // Large number to get all
        sortBy,
        sortOrder
      });
      
      if (organizationId) params.append('organizationId', organizationId);
      if (teamId) params.append('teamId', teamId);
      if (accountId) params.append('accountId', accountId);
      if (transactionType) params.append('transactionType', transactionType);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (search) params.append('search', search);

      // Create CSV content
      const csvRows = [];
      csvRows.push(['Date', 'Type', 'Amount', 'Balance Before', 'Balance After', 'Description', 'Performed By'].join(','));
      
      transactions.forEach(t => {
        const date = new Date(t.timestamp).toLocaleString();
        const amount = (t.amount || 0).toFixed(2);
        const balanceBefore = (t.balanceBefore || 0).toFixed(2);
        const balanceAfter = (t.balanceAfter || 0).toFixed(2);
        const description = (t.description || '').replace(/"/g, '""');
        const performedBy = t.performedBy?.email || '';
        csvRows.push([date, t.transactionType, amount, balanceBefore, balanceAfter, `"${description}"`, performedBy].join(','));
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credit-history-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('Failed to export data');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  const getTransactionTypeColor = (type) => {
    const colors = {
      'PURCHASE': '#059669',
      'ALLOCATION': '#2563eb',
      'CONSUMPTION': '#dc2626',
      'EXPIRATION': '#f59e0b',
      'RECLAMATION': '#7c3aed',
      'REFUND': '#10b981',
      'ADJUSTMENT': '#6b7280'
    };
    return colors[type] || '#6b7280';
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="admin-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 className="admin-header">Credit History</h2>
        <button
          onClick={handleExport}
          disabled={loading || transactions.length === 0}
          style={{
            padding: '8px 16px',
            background: '#059669',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: loading || transactions.length === 0 ? 'not-allowed' : 'pointer',
            fontSize: 14,
            fontWeight: 500,
            opacity: loading || transactions.length === 0 ? 0.5 : 1
          }}
        >
          Export CSV
        </button>
      </div>

      {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}

      {/* Filters */}
      <div className="admin-controls" style={{ flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: '#374151', minWidth: 80 }}>Type:</span>
          <select
            value={transactionType}
            onChange={e => { setTransactionType(e.target.value); setPage(1); }}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#fff',
              cursor: 'pointer',
              minWidth: '150px'
            }}
          >
            <option value="">All Types</option>
            <option value="PURCHASE">Purchase</option>
            <option value="ALLOCATION">Allocation</option>
            <option value="CONSUMPTION">Consumption</option>
            <option value="EXPIRATION">Expiration</option>
            <option value="RECLAMATION">Reclamation</option>
            <option value="REFUND">Refund</option>
            <option value="ADJUSTMENT">Adjustment</option>
          </select>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: '#374151', minWidth: 80 }}>Start:</span>
          <input
            type="date"
            value={startDate}
            onChange={e => { setStartDate(e.target.value); setPage(1); }}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#fff'
            }}
          />
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: '#374151', minWidth: 80 }}>End:</span>
          <input
            type="date"
            value={endDate}
            onChange={e => { setEndDate(e.target.value); setPage(1); }}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#fff'
            }}
          />
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: '#374151', minWidth: 80 }}>Search:</span>
          <input
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Description or metadata..."
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#fff',
              width: '200px'
            }}
          />
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Rows:</span>
          <select
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#fff',
              cursor: 'pointer'
            }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </label>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading...</div>
      ) : transactions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>No transactions found</div>
      ) : (
        <>
          <div className="table-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('timestamp')}
                  >
                    Date <SortIcon column="timestamp" />
                  </th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('type')}
                  >
                    Type <SortIcon column="type" />
                  </th>
                  <th 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('amount')}
                  >
                    Amount <SortIcon column="amount" />
                  </th>
                  <th>Balance Before</th>
                  <th>Balance After</th>
                  <th>Description</th>
                  <th>Performed By</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr 
                    key={t.transactionId}
                    onClick={() => handleRowClick(t.transactionId)}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                  >
                    <td>{formatDate(t.timestamp)}</td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: getTransactionTypeColor(t.transactionType) + '20',
                        color: getTransactionTypeColor(t.transactionType),
                        fontSize: 12,
                        fontWeight: 500
                      }}>
                        {t.transactionType}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600, color: t.amount >= 0 ? '#059669' : '#dc2626' }}>
                      {t.amount >= 0 ? '+' : ''}{formatCurrency(t.amount)}
                    </td>
                    <td>{formatCurrency(t.balanceBefore)}</td>
                    <td>{formatCurrency(t.balanceAfter)}</td>
                    <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.description || '-'}
                    </td>
                    <td>{t.performedBy?.email || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="admin-pager" style={{ marginTop: 16, justifyContent: 'space-between' }}>
            <div style={{ color: '#6b7280', fontSize: 14 }}>
              Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total} transactions
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  background: page === 1 ? '#f3f4f6' : '#fff',
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page * pageSize >= total}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  background: page * pageSize >= total ? '#f3f4f6' : '#fff',
                  cursor: page * pageSize >= total ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedTransaction && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <button className="modal-close" onClick={() => setShowDetailModal(false)}>×</button>
            <h2 style={{ marginTop: 0, marginBottom: 20 }}>Transaction Details</h2>
            
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <strong style={{ color: '#6b7280', fontSize: 12 }}>Transaction ID</strong>
                <div style={{ marginTop: 4, fontSize: 14 }}>{selectedTransaction.transactionId}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <strong style={{ color: '#6b7280', fontSize: 12 }}>Type</strong>
                  <div style={{ marginTop: 4 }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      background: getTransactionTypeColor(selectedTransaction.transactionType) + '20',
                      color: getTransactionTypeColor(selectedTransaction.transactionType),
                      fontSize: 12,
                      fontWeight: 500
                    }}>
                      {selectedTransaction.transactionType}
                    </span>
                  </div>
                </div>
                <div>
                  <strong style={{ color: '#6b7280', fontSize: 12 }}>Amount</strong>
                  <div style={{ marginTop: 4, fontSize: 16, fontWeight: 600, color: selectedTransaction.amount >= 0 ? '#059669' : '#dc2626' }}>
                    {selectedTransaction.amount >= 0 ? '+' : ''}{formatCurrency(selectedTransaction.amount)}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <strong style={{ color: '#6b7280', fontSize: 12 }}>Balance Before</strong>
                  <div style={{ marginTop: 4 }}>{formatCurrency(selectedTransaction.balanceBefore)}</div>
                </div>
                <div>
                  <strong style={{ color: '#6b7280', fontSize: 12 }}>Balance After</strong>
                  <div style={{ marginTop: 4 }}>{formatCurrency(selectedTransaction.balanceAfter)}</div>
                </div>
              </div>

              <div>
                <strong style={{ color: '#6b7280', fontSize: 12 }}>Timestamp</strong>
                <div style={{ marginTop: 4 }}>{formatDate(selectedTransaction.timestamp)}</div>
              </div>

              {selectedTransaction.description && (
                <div>
                  <strong style={{ color: '#6b7280', fontSize: 12 }}>Description</strong>
                  <div style={{ marginTop: 4 }}>{selectedTransaction.description}</div>
                </div>
              )}

              {selectedTransaction.performedBy && (
                <div>
                  <strong style={{ color: '#6b7280', fontSize: 12 }}>Performed By</strong>
                  <div style={{ marginTop: 4 }}>
                    {selectedTransaction.performedBy.firstName} {selectedTransaction.performedBy.lastName} ({selectedTransaction.performedBy.email})
                  </div>
                </div>
              )}

              {selectedTransaction.sourceAccount && (
                <div>
                  <strong style={{ color: '#6b7280', fontSize: 12 }}>Source Account</strong>
                  <div style={{ marginTop: 4 }}>
                    {selectedTransaction.sourceAccount.entityType} - {selectedTransaction.sourceAccount.entityId}
                    {selectedTransaction.sourceAccount.balance !== undefined && (
                      <span style={{ color: '#6b7280', marginLeft: 8 }}>
                        (Balance: {formatCurrency(selectedTransaction.sourceAccount.balance)})
                      </span>
                    )}
                  </div>
                </div>
              )}

              {selectedTransaction.destinationAccount && (
                <div>
                  <strong style={{ color: '#6b7280', fontSize: 12 }}>Destination Account</strong>
                  <div style={{ marginTop: 4 }}>
                    {selectedTransaction.destinationAccount.entityType} - {selectedTransaction.destinationAccount.entityId}
                    {selectedTransaction.destinationAccount.balance !== undefined && (
                      <span style={{ color: '#6b7280', marginLeft: 8 }}>
                        (Balance: {formatCurrency(selectedTransaction.destinationAccount.balance)})
                      </span>
                    )}
                  </div>
                </div>
              )}

              {selectedTransaction.expiresAt && (
                <div>
                  <strong style={{ color: '#6b7280', fontSize: 12 }}>Expires At</strong>
                  <div style={{ marginTop: 4 }}>{formatDate(selectedTransaction.expiresAt)}</div>
                </div>
              )}

              {selectedTransaction.metadata && (
                <div>
                  <strong style={{ color: '#6b7280', fontSize: 12 }}>Metadata</strong>
                  <pre style={{ 
                    marginTop: 4, 
                    padding: 12, 
                    background: '#f9fafb', 
                    borderRadius: '6px', 
                    fontSize: 12,
                    overflow: 'auto',
                    maxHeight: 200
                  }}>
                    {typeof selectedTransaction.metadata === 'string' 
                      ? selectedTransaction.metadata 
                      : JSON.stringify(selectedTransaction.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditHistory;

